import { useCallback, useEffect, useState } from "react"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//primeReact
import { Dialog } from "primereact/dialog"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikNumberInput from "../../../helpers/formikInputs/FormikNumberInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"

//Housing components
import {
  housingApiCreateUserChargePost,
  housingApiEditUserChargePost,
} from "../housingApi"
import { DateTime } from "luxon"
import { useContext } from "react"
import { LogError } from "../../../helpers/logger"
import HousingOtherChargeDeleteForm from "./HousingOtherChargeDeleteForm"

export default function HousingOtherChargeForm({
  showForm,
  onHideForm,
  userChargeEntry,
  houseList,
  houseParticipantList,
  activeHouseParticipants,
  onChargeCreated,
  onChargeUpdated,
}) {
  const userCtx = useContext(UserContext)

  const getNewCharge = useCallback(() => {
    return {
      houseId: undefined,
      organizationId: userCtx.session.user.organization_id,
      participantId: undefined,
      description: "",
      dueDate: new Date(),
      graceDays: 0,
      amountDue: 0.0,
      userId: userCtx.session.user.staff_id,
    }
  }, [userCtx.session.user.organization_id, userCtx.session.user.staff_id])

  const [participantHouses, setParticipantHouses] = useState(houseList)
  const [houseParticipants, setHouseParticipants] = useState(
    activeHouseParticipants,
  )
  const [newCharge, setNewCharge] = useState(getNewCharge())
  const [showDeleteForm, setShowDeleteForm] = useState(false)

  useEffect(() => {
    let charge = getNewCharge()

    if (!!userChargeEntry) {
      charge.id = userChargeEntry.rent.id
      charge.houseId = userChargeEntry.rent.house.id
      charge.organizationId = userChargeEntry.organizationId
      charge.participantId = userChargeEntry.rent.participantId
      charge.description = userChargeEntry.rent.description
      charge.dueDate = DateTime.fromISO(userChargeEntry.rent.dueDate, {
        zone: "utc",
      })
        .toLocal()
        .toJSDate()
      charge.graceDays = userChargeEntry.rent.graceDays
      charge.amountDue = userChargeEntry.rent.amountDue
      charge.userId = userChargeEntry.rent.userId
    }

    setNewCharge(charge)
  }, [getNewCharge, userChargeEntry])

  const validationSchema = Yup.object().shape({
    dueDate: Yup.date().required("The payment date is required"),
    amountDue: Yup.number().min(0.01).required("Please enter the amount"),
    houseId: Yup.string().required("Please select a House"),
    participantId: Yup.string().required("Please select a participant"),
  })

  const submitCharge = async (values) => {
    try {
      let submitValues = { ...values }

      //Convert dueDate to be end of day and UTC
      submitValues.dueDate = DateTime.fromJSDate(submitValues.dueDate)
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .toUTC()
        .toISO()

      if (!values.id) {
        const result = await housingApiCreateUserChargePost(
          userCtx.tokens.getHousingToken(),
          submitValues,
        )

        if (!!result) {
          onChargeCreated(result)
        }
      } else {
        const result = await housingApiEditUserChargePost(
          userCtx.tokens.getHousingToken(),
          submitValues,
        )

        if (!!result) {
          if (!!onChargeUpdated) {
            onChargeUpdated(result)
          } else {
            onChargeCreated(result)
          }
        }
      }
    } catch (error) {
      userCtx.setNotificationError("Unable to save charge")
      LogError("Unable to save user charge", error)
      return false
    }
  }

  return (
    <>
      <HousingOtherChargeDeleteForm
        showForm={showDeleteForm}
        chargeEntry={userChargeEntry}
        onHideForm={() => {
          setShowDeleteForm(false)
        }}
        onDeleted={(result) => {
          setShowDeleteForm(false)
          if (!!onChargeUpdated) {
            onChargeUpdated(result)
          } else {
            onChargeCreated(result)
          }
        }}
      />
      <Dialog
        header={`New house charge`}
        visible={showForm}
        onHide={() => {
          onHideForm()
        }}
        style={{ width: "400px" }}
      >
        <Formik
          initialValues={newCharge}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values) => {
            return submitCharge(values)
          }}
        >
          {({ isSubmitting, values, dirty, setFieldValue, errors }) => (
            <Form>
              <FormikDropDown
                name={"houseId"}
                label={"House"}
                helpText={"The house for which this charge is recorded"}
                placeHolderValue="Please select a house"
                required={true}
                options={participantHouses}
                optionValue={"id"}
                optionLabel={"name"}
                disabled={isSubmitting || !!values.id}
                onChangeCallback={(value) => {
                  if (!!value && !!houseParticipantList) {
                    const house = houseList.find((house) => house.id === value)

                    setHouseParticipants(
                      houseParticipantList.filter((participant) =>
                        house.activeParticipants.some(
                          (houseParticipant) =>
                            houseParticipant === participant.participant_id,
                        ),
                      ),
                    )

                    if (
                      !!values.participantId &&
                      !house.activeParticipants.some(
                        (houseParticipant) =>
                          houseParticipant === values.participantId,
                      )
                    ) {
                      setFieldValue("participantId", undefined)
                    }
                  }
                }}
              />
              <FormikDropDown
                name={"participantId"}
                label={"Participant"}
                helpText={"The participant for which this charge is recorded"}
                options={
                  !!values.id && !!houseParticipantList
                    ? houseParticipantList
                    : houseParticipants
                }
                optionValue={"participant_id"}
                optionLabel={"name2"}
                required={true}
                disabled={isSubmitting || !!values.id}
                onChangeCallBack={(value) => {
                  if (value.length > 0) {
                    setParticipantHouses(
                      houseList.filter((house) =>
                        house.activeParticipants.some(
                          (participant) => value === participant,
                        ),
                      ),
                    )
                  } else {
                    setParticipantHouses(houseList)
                  }
                }}
              />
              <FormikTextInput
                name={"description"}
                label={"Charge description"}
                helpText={"Describe what this charge is for"}
                required={true}
                disabled={isSubmitting}
              />
              <FormikCalendar
                name={"dueDate"}
                label={"Due date"}
                helpText={"The date on which this charge will be due"}
                required={true}
                disabled={isSubmitting}
              />

              <FormikNumberInput
                name={"amountDue"}
                label={"Amount"}
                helpText={"The amount payable"}
                minValue={0}
                minFractionDigits={2}
                currencyCode={"USD"}
                required={true}
                disabled={isSubmitting}
              />
              <FormikNumberInput
                name={"graceDays"}
                label={"Payment grace days"}
                helpText={"The grace period before the charge becomes over due"}
                minFractionDigits={0}
                disabled={isSubmitting}
              />
              <div style={{ float: "right" }}>
                <button
                  type="submit"
                  className="btn"
                  disabled={isSubmitting || !dirty}
                >
                  Save
                </button>
                {!!values.id ? (
                  <button
                    type="button"
                    className="btn"
                    style={{ marginLeft: "10px" }}
                    onClick={() => {
                      setShowDeleteForm(true)
                    }}
                    disabled={isSubmitting}
                  >
                    Delete
                  </button>
                ) : null}

                <button
                  type="button"
                  className="btn"
                  style={{ marginLeft: "10px" }}
                  onClick={() => {
                    onHideForm()
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}
